import { PropsWithChildren, useEffect, useMemo } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { isAxiosError } from 'axios';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { useVerifyAuth } from '@/hooks/dbui/useVerifyAuth';
import { SuspenseErrorBoundary } from '@/components/errorBoundary/SuspenseErrorBoundary';
import { useStandaloneAuthentication } from '@/hooks/authentication/useStandaloneAuthentication';
import { useInstanceId } from '@/contexts/InstanceIdContext';

const VerifyAuth: React.FC<PropsWithChildren> = ({ children }) => {
  const { data } = useVerifyAuth();
  const { refreshAuthentication } = useStandaloneAuthentication();
  const { instanceId } = useInstanceId();

  useEffect(() => {
    if (
      !data.isDatabricksAuthed ||
      (instanceId && instanceId !== data.daslWorkspaceId)
    ) {
      refreshAuthentication();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return !data.isDatabricksAuthed ||
    (instanceId && instanceId !== data.daslWorkspaceId) ? null : (
    <>{children}</>
  );
};

const AuthErrorBoundaryFallback: React.FC<FallbackProps> = (props) => {
  const isAuthenticationError = useMemo(
    () => isAxiosError(props.error) && props.error.response?.status === 401,
    [props.error],
  );

  // If there is no instance ID, then we should not allow retries stince they will all fail

  return (
    <main className="flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      {!isAuthenticationError ? (
        <GlobalError
          title="Something went wrong"
          description={props.error.message}
          onRetry={async () => props.resetErrorBoundary()}
          retryText="Retry"
        />
      ) : null}
    </main>
  );
};

export default function AuthHandler({ children }: PropsWithChildren) {
  return (
    <SuspenseErrorBoundary errorBoundaryFallback={AuthErrorBoundaryFallback}>
      <VerifyAuth>{children}</VerifyAuth>
    </SuspenseErrorBoundary>
  );
}
